/**
 * chatClipboard.ts — what leaves the chat transcript when someone copies.
 *
 * Owner report 2026-08-26, the copy half:
 *
 *   "when a user copies and pastes a message from the chat interface response
 *    and pastes it back into the chat, it gets formatted in a way that hangs
 *    off the screen … and also the copy (select all text and copy the text)
 *    when users do that it should not be a formatted text but plain text copy
 *    unlike what is occurring right now. there should be some formatting like
 *    font, size, italics, underline, strikethrough, and much more but it
 *    should not copy the color scheme, indentation and all that."
 *
 * There was no copy handler at all — select-all-copy was 100% browser default,
 * so the clipboard's `text/html` flavour was the raw serialised DOM: every
 * styled-components class of the message bubble and its row, plus whatever
 * inline geometry the browser annotated. Paste that anywhere inside the app and
 * the classes resolve against the app's own stylesheet again, which is how a
 * copied message came back as a gold bubble hanging off the right edge.
 *
 * THE RULE, in the owner's own division: MEANING travels, CHROME does not.
 *   • Meaning — bold, italic, underline, strikethrough, headings, lists,
 *     quotes, code, tables, links, and the typographic properties that
 *     describe the text (weight, style, size, family, decoration, spacing,
 *     alignment).
 *   • Chrome — classes, colours, backgrounds, padding, margins, widths,
 *     borders, shadows, layout. None of it describes the writing; all of it
 *     describes the bubble the writing happened to be sitting in.
 *
 * Both clipboard flavours are written, and BOTH matter:
 *   • `text/html` — the semantic subset, so pasting into a rich editor (this
 *     composer, an email, a doc) keeps the bold and the lists.
 *   • `text/plain` — a faithful block-aware rendering, so pasting into a
 *     terminal, a code editor or a plain-text field gives readable text
 *     instead of one run-on line.
 *
 * Pure and DOM-only (no React, no app imports) so the rules are unit-tested
 * directly, in the house style of pasteSanitizer.
 */

/**
 * Inline style properties that survive a copy. Everything else is dropped.
 *
 * An ALLOWLIST rather than a denylist, deliberately and unlike the paste side:
 * a property nobody has thought about yet is far more likely to be chrome than
 * meaning, and the cost of wrongly dropping one is a slightly plainer paste,
 * while the cost of wrongly keeping one is the bug this file exists to fix.
 */
const KEPT_COPY_STYLE_PROPERTIES: readonly string[] = [
  'font-weight',
  'font-style',
  'font-size',
  'font-family',
  'font-variant',
  'text-decoration',
  'text-decoration-line',
  'text-decoration-style',
  'text-transform',
  'letter-spacing',
  'line-height',
  // A property of the writing, not of the box — a centred heading stays centred.
  'text-align',
];

/**
 * Elements dropped entirely, with their contents.
 *
 * Interactive chrome the transcript paints INSIDE a bubble: the per-message
 * copy button, the stop button, workflow CTAs. A pasted `<button>` is never
 * what anyone meant to copy, and its label ("copy", "stop") reads as stray
 * words in the middle of the text.
 */
const DROPPED_COPY_SELECTORS =
  'button, svg, input, select, textarea, script, style, noscript, [aria-hidden="true"]';

/** Block-level tags that force a line break in the plain-text rendering. */
const COPY_BLOCK_TAGS: ReadonlySet<string> = new Set([
  'DIV', 'P', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'PRE', 'HR', 'TABLE', 'THEAD',
  'TBODY', 'TFOOT', 'TR', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'SECTION',
  'ARTICLE', 'HEADER', 'FOOTER', 'ASIDE', 'MAIN', 'NAV', 'FIGURE',
  'FIGCAPTION', 'DL', 'DT', 'DD',
]);

export interface ChatClipboardPayload {
  /** The semantic subset — meaning without chrome. */
  html: string;
  /** Block-aware plain text. */
  text: string;
}

/**
 * Strip chrome from a copied fragment, in place.
 *
 * Exported so the copy handler and the tests operate on exactly the same pass.
 */
export function stripChromeFromFragment(root: Element): void {
  // 1. Drop non-content elements outright (contents included — a button's
  //    label is chrome too).
  root.querySelectorAll(DROPPED_COPY_SELECTORS).forEach((el) => el.remove());

  // 2. Every class goes. This is the one that matters: the gold bubble
  //    background is a class rule marked `!important`, so nothing an inline
  //    style pass could do would neutralise it.
  root.querySelectorAll('[class]').forEach((el) => el.removeAttribute('class'));

  // 3. Reduce inline styles to the typographic allowlist. An element left with
  //    nothing loses the attribute rather than carrying an empty one.
  root.querySelectorAll('[style]').forEach((el) => {
    const style = (el as HTMLElement).style;
    const kept: string[] = [];
    for (const property of KEPT_COPY_STYLE_PROPERTIES) {
      const value = style.getPropertyValue(property);
      if (value) kept.push(`${property}: ${value}`);
    }
    if (kept.length) el.setAttribute('style', kept.join('; '));
    else el.removeAttribute('style');
  });

  // 4. Drop the app's own presentational and behavioural data attributes so a
  //    copied fragment cannot re-arm app behaviour when pasted back in.
  //    `href` on anchors is content and is deliberately untouched.
  root.querySelectorAll('*').forEach((el) => {
    for (const name of el.getAttributeNames()) {
      if (name === 'style' || name === 'href' || name === 'title' || name === 'lang') continue;
      if (name === 'colspan' || name === 'rowspan') continue; // table structure is meaning
      el.removeAttribute(name);
    }
  });
}

/**
 * Render a cleaned fragment as block-aware plain text.
 *
 * Written by hand rather than read off `textContent`, because `textContent`
 * concatenates block elements with no separator at all: a transcript copied
 * that way arrives as one unbroken line with words fused across paragraph
 * boundaries ("…refreshing.That's for Justin."). List items get a "- " marker
 * so a copied list still reads as a list in a plain-text destination.
 */
export function fragmentToPlainText(root: Node): string {
  const parts: string[] = [];

  // Inside <pre> (and code blocks) whitespace IS the content — collapsing it
  // flattened every copied code block to one line (2026-08-27 review fix).
  let preserveDepth = 0;

  const walk = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (preserveDepth > 0) {
        const raw = node.textContent || '';
        if (raw) parts.push(raw);
        return;
      }
      // Collapse runs of whitespace the way a browser renders them, so the
      // source's indentation does not become text.
      const value = (node.textContent || '').replace(/\s+/g, ' ');
      if (value) parts.push(value);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as Element;
    const tag = el.tagName;

    if (tag === 'BR') {
      parts.push('\n');
      return;
    }
    if (tag === 'HR') {
      parts.push('\n---\n');
      return;
    }

    const isBlock = COPY_BLOCK_TAGS.has(tag);
    if (isBlock) parts.push('\n');
    if (tag === 'LI') parts.push('- ');
    const preserves = tag === 'PRE';
    if (preserves) preserveDepth += 1;
    // A table cell is not a block (a row is), so cells stay on one line
    // separated by a tab — which is what a spreadsheet expects on paste.
    if (tag === 'TD' || tag === 'TH') {
      if (el.previousElementSibling) parts.push('\t');
    }

    el.childNodes.forEach(walk);

    if (preserves) preserveDepth -= 1;
    if (isBlock) parts.push('\n');
  };

  root.childNodes.forEach(walk);

  // NOTE the cleanup deliberately keeps LEADING spaces after a newline —
  // that is code indentation inside <pre>. Only trailing space before a
  // newline and blank-line runs collapse.
  return parts
    .join('')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Build both clipboard flavours from the HTML of a copied selection.
 *
 * Returns empty strings when there is nothing to copy, so the caller can fall
 * through to the browser default rather than writing an empty clipboard.
 */
export function buildChatClipboardPayload(fragmentHtml: string): ChatClipboardPayload {
  if (!fragmentHtml || !fragmentHtml.trim()) return { html: '', text: '' };
  try {
    // <template> content parses WITHOUT the normal element-context rules
    // (2026-08-27 review fix): a selection that starts inside a table
    // serializes as bare <tr>/<td> markup, and parsing that inside a <div>
    // makes the HTML parser DROP the table tags entirely — the cells' text
    // fused into one run with no separator. Template content keeps them.
    const doc = new DOMParser().parseFromString(
      `<template data-copy-root>${fragmentHtml}</template>`,
      'text/html',
    );
    const template = doc.querySelector('template[data-copy-root]') as HTMLTemplateElement | null;
    if (!template) return { html: '', text: '' };
    const root = doc.createElement('div');
    root.appendChild(template.content.cloneNode(true));

    stripChromeFromFragment(root);

    const text = fragmentToPlainText(root);
    // A selection that turns out to be pure chrome (the user dragged across a
    // button row) leaves nothing worth putting on the clipboard.
    if (!text) return { html: '', text: '' };
    return { html: root.innerHTML, text };
  } catch {
    // No DOM, or markup we could not parse. Empty payload ⇒ the caller leaves
    // the browser's own copy alone, which is strictly better than clearing it.
    return { html: '', text: '' };
  }
}

/**
 * Rewrite a copy event's clipboard with the semantic payload.
 *
 * Returns true when it took over. False means "nothing was done" and the
 * browser's default copy stands — the deliberate outcome for an empty
 * selection, a selection of pure chrome, or any environment where the
 * clipboard is not writable.
 */
export function applyChatClipboardPayload(
  selectionHtml: string,
  clipboard: { setData: (type: string, value: string) => void } | null | undefined,
): boolean {
  if (!clipboard) return false;
  const payload = buildChatClipboardPayload(selectionHtml);
  if (!payload.text) return false;
  try {
    clipboard.setData('text/plain', payload.text);
    clipboard.setData('text/html', payload.html);
    return true;
  } catch {
    return false;
  }
}

/** Serialize the current selection to HTML. Empty string when there is none. */
export function selectionToHtml(selection: Selection | null | undefined): string {
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return '';
  try {
    const container = document.createElement('div');
    for (let i = 0; i < selection.rangeCount; i += 1) {
      container.appendChild(selection.getRangeAt(i).cloneContents());
    }
    return container.innerHTML;
  } catch {
    return '';
  }
}
